import * as THREE from 'three';
import type { CityMap } from '../game/map';

const DIST = 3.4;
const HEIGHT = 1.9;
const SHOULDER = 0.42;
/** Focus (her noticing him): how fast the camera swings to it, how much height it frames, the tightest zoom (degrees). */
const FOCUS_RATE = 4;
const FOCUS_FRAME = 3.2;
const FOCUS_MIN_FOV = 11;

function wrap(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * Over-the-shoulder follow camera. Touch-drag look orbits within limits and drifts
 * back; looking back swings the camera around to face whatever is behind her.
 * A focus point (her noticing him) swings it round over her shoulder toward
 * that point and zooms in on it.
 */
export class CameraRig {
  yawOffset = 0;
  pitch = 0.1;
  lookBackBlend = 0;
  private idleLook = 0;
  private distance = DIST;
  private orbit = 0;
  private readonly desired = new THREE.Vector3();
  private readonly lookAt = new THREE.Vector3();
  private readonly smoothLook = new THREE.Vector3();
  private readonly head = new THREE.Vector3();
  private shakeTime = 0;
  private focusBlend = 0;
  private readonly focusPoint = new THREE.Vector3();

  constructor(private readonly camera: THREE.PerspectiveCamera) {}

  addLook(dx: number, dy: number): void {
    if (dx !== 0 || dy !== 0) this.idleLook = 0;
    this.yawOffset = THREE.MathUtils.clamp(this.yawOffset - dx * 0.0026, -2.0, 2.0);
    this.pitch = THREE.MathUtils.clamp(this.pitch + dy * 0.0018, -0.3, 0.55);
  }

  snap(target: THREE.Vector3, heading: number): void {
    this.orbit = heading;
    this.yawOffset = 0;
    this.lookBackBlend = 0;
    this.focusBlend = 0;
    this.distance = DIST;
    this.compute(target, heading, 1, null);
    this.camera.position.copy(this.desired);
    this.smoothLook.copy(this.lookAt);
    this.camera.lookAt(this.smoothLook);
  }

  update(
    dt: number,
    target: THREE.Vector3,
    heading: number,
    lookBack: boolean,
    map: CityMap,
    shake: number,
    fov: number,
    focus: THREE.Vector3 | null = null,
  ): void {
    this.idleLook += dt;
    if (this.idleLook > 1.4) {
      const k = Math.exp(-dt * 1.4);
      this.yawOffset *= k;
      this.pitch = 0.1 + (this.pitch - 0.1) * k;
    }
    this.lookBackBlend += ((lookBack ? 1 : 0) - this.lookBackBlend) * Math.min(1, dt * 6.5);

    if (focus) this.focusPoint.copy(focus);
    this.focusBlend += ((focus ? 1 : 0) - this.focusBlend) * Math.min(1, dt * FOCUS_RATE);
    const focusing = this.focusBlend > 0.001;

    // Orbit angle follows heading with a little lag (smooth around corners).
    let wanted = heading + this.yawOffset + this.lookBackBlend * Math.PI * 0.94;
    if (focusing) {
      // Behind her, on the far side from the focus point, so it is seen over her shoulder.
      const toward = Math.atan2(target.x - this.focusPoint.x, target.z - this.focusPoint.z);
      wanted += wrap(toward - wanted) * this.focusBlend;
    }
    let diff = wanted - this.orbit;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    this.orbit += diff * Math.min(1, dt * 7);

    this.compute(target, this.orbit, dt, map);
    if (focusing) this.lookAt.lerp(this.focusPoint, this.focusBlend);
    this.camera.position.lerp(this.desired, Math.min(1, dt * 10));
    this.smoothLook.lerp(this.lookAt, Math.min(1, dt * 12));

    this.shakeTime += dt;
    if (shake > 0) {
      const t = this.shakeTime;
      this.camera.position.x += (Math.sin(t * 23.1) + Math.sin(t * 13.7)) * 0.006 * shake;
      this.camera.position.y += (Math.sin(t * 19.3) + Math.sin(t * 7.9)) * 0.006 * shake;
    }
    this.camera.lookAt(this.smoothLook);
    if (focusing) {
      // Zoom until about FOCUS_FRAME metres of height around the point fills the view.
      const d = Math.max(1, this.camera.position.distanceTo(this.focusPoint));
      const zoom = Math.max(FOCUS_MIN_FOV, THREE.MathUtils.radToDeg(2 * Math.atan(FOCUS_FRAME / 2 / d)));
      fov += (Math.min(fov, zoom) - fov) * this.focusBlend;
    }
    if (Math.abs(this.camera.fov - fov) > 0.01) {
      this.camera.fov += (fov - this.camera.fov) * Math.min(1, dt * 3);
      this.camera.updateProjectionMatrix();
    }
  }

  private compute(target: THREE.Vector3, orbit: number, dt: number, map: CityMap | null): void {
    const backX = Math.sin(orbit);
    const backZ = Math.cos(orbit);
    const rightX = Math.cos(orbit);
    const rightZ = -Math.sin(orbit);
    const shoulder = SHOULDER * (1 - this.lookBackBlend * 1.6);
    this.head.set(target.x, target.y + 1.5, target.z);

    // Pull the camera in if a wall is between her and the desired spot.
    const ideal = DIST * (1 - this.lookBackBlend * 0.15);
    let allowed = ideal;
    const steps = 14;
    for (let i = 1; i <= steps; i += 1) {
      const d = (ideal * i) / steps;
      const x = target.x + backX * d + rightX * shoulder * (i / steps);
      const z = target.z + backZ * d + rightZ * shoulder * (i / steps);
      if (map && !map.isWalkable(x, z, 0.95)) {
        allowed = Math.max(0.9, (ideal * (i - 1)) / steps - 0.15);
        break;
      }
    }
    const rate = allowed < this.distance ? 14 : 3;
    this.distance += (allowed - this.distance) * Math.min(1, dt * rate);

    const h = HEIGHT + this.pitch * 1.6 - this.lookBackBlend * 0.2;
    const sh = shoulder * Math.min(1, this.distance / DIST);
    this.desired.set(
      target.x + backX * this.distance + rightX * sh,
      target.y + h,
      target.z + backZ * this.distance + rightZ * sh,
    );
    const ahead = 4;
    this.lookAt.set(
      target.x - backX * ahead + rightX * sh * 0.4,
      target.y + 1.45 - this.pitch * 2.2,
      target.z - backZ * ahead + rightZ * sh * 0.4,
    );
  }
}
